import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Award, BookOpen, Briefcase, CalendarDays, CheckCircle2, Clock, Download, GraduationCap, Layers, MessageCircle, MonitorSmartphone, Users, Wrench } from "lucide-react";
import { api, inr, whatsappLink } from "@/lib/api";
import SEO from "@/components/SEO";
import CourseCard from "@/components/CourseCard";
import EnquiryDialog from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setCourse(null);
    setNotFound(false);
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!course) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "course-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "Course",
      name: course.title, description: course.short_description,
      provider: { "@type": "EducationalOrganization", name: "XTM TECHNOLOGIES — The FutureSkills Academy", address: "Tarajan, Jorhat, Assam" },
    });
    document.head.appendChild(script);
    return () => document.getElementById("course-jsonld")?.remove();
  }, [course]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="course-not-found">
        <p className="text-slate-400">Course not found or not currently published.</p>
        <Button asChild className="bg-orange-600 hover:bg-orange-700 text-white rounded-full"><Link to="/courses">Browse Courses</Link></Button>
      </div>
    );
  }
  if (!course) return <div className="min-h-[60vh] flex items-center justify-center text-slate-500" data-testid="course-loading">Loading…</div>;

  const discounted = course.discount > 0 ? Math.round(course.fees * (1 - course.discount / 100)) : course.fees;
  const waMsg = `Hello XTM TECHNOLOGIES, I am interested in the ${course.title} program. Please share course details.`;

  return (
    <div className="bg-navy-base" data-testid="course-detail-page">
      <SEO title={course.seo_title || course.title} description={course.seo_description || course.short_description} />

      <section className="relative hero-glow overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <nav className="text-xs text-slate-500 mb-4" data-testid="course-breadcrumb">
              <Link to="/" className="hover:text-orange-400">Home</Link> / <Link to="/courses" className="hover:text-orange-400">Courses</Link> /{" "}
              <Link to={`/courses/category/${course.category_slug}`} className="hover:text-orange-400">{course.category?.name || course.category_slug}</Link>
            </nav>
            <div className="flex flex-wrap gap-2 mb-4">
              {(course.badges || []).map((b) => (
                <span key={b} className="text-[10px] font-semibold uppercase tracking-wider border border-orange-500/40 text-orange-300 rounded-full px-2.5 py-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500 mr-1 align-middle" />{b}
                </span>
              ))}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4" data-testid="course-title">{course.title}</h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8" data-testid="course-short-desc">{course.short_description}</p>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8" data-testid="course-quick-facts">
              {[[Clock, "Duration", course.duration], [GraduationCap, "Eligibility", course.eligibility], [MonitorSmartphone, "Mode", course.mode], [Layers, "Level", course.level], [Award, "Certificate", "Verifiable QR"]].map(([Icon, label, value]) => (
                <div key={label} className="bg-card/70 border border-white/10 rounded-xl p-3.5">
                  <Icon className="w-4 h-4 text-orange-400 mb-2" />
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
                  <div className="text-xs font-semibold text-white mt-0.5 leading-snug">{value}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-7 transition-colors" data-testid="course-enroll-btn">
                <Link to={`/admissions?course=${course.slug}`}>ENROLL NOW</Link>
              </Button>
              <EnquiryDialog type="brochure" course={course.title} title="Download Brochure" trigger={
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full px-7 transition-colors" data-testid="course-brochure-btn">
                  <Download className="w-4 h-4 mr-2" /> Brochure
                </Button>
              } />
              <Button asChild size="lg" variant="outline" className="border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 rounded-full px-7 transition-colors" data-testid="course-whatsapp-btn">
                <a href={whatsappLink(waMsg)} target="_blank" rel="noreferrer"
                  onClick={() => api.post("/leads/whatsapp-click", { context: "course-page", course: course.title }).catch(() => {})}>
                  <MessageCircle className="w-4 h-4 mr-2" /> Counsellor
                </a>
              </Button>
            </div>
          </div>

          <div className="lg:sticky lg:top-28 h-fit">
            <img src={course.image} alt={course.title} className="rounded-2xl w-full h-56 object-cover border border-white/10 mb-4" />
            <div className="bg-card border border-white/10 rounded-2xl p-6" data-testid="course-fee-card">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Course Fee</div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-display text-3xl font-extrabold text-white" data-testid="course-fee">{inr(discounted)}</span>
                {course.discount > 0 && <span className="text-sm text-slate-500 line-through">{inr(course.fees)}</span>}
              </div>
              {course.discount > 0 && <div className="text-xs font-semibold text-orange-400 mb-3">{course.discount}% early-bird discount applied</div>}
              <p className="text-xs text-slate-500 mb-4">Instalment options available — ask a counsellor. Certification: {course.certification}</p>
              <Button asChild className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl transition-colors" data-testid="fee-card-enroll-btn">
                <Link to={`/admissions?course=${course.slug}`}>Apply for Admission</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <Tabs defaultValue="curriculum" data-testid="course-tabs">
            <TabsList className="bg-navy-surface border border-white/10 flex-wrap h-auto gap-1 p-1">
              {[["curriculum", "Curriculum"], ["tools", "Tools"], ["projects", "Projects"], ["careers", "Careers"], ["outcomes", "Outcomes"]].map(([v, l]) => (
                <TabsTrigger key={v} value={v} className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-slate-300 text-xs sm:text-sm" data-testid={`tab-${v}`}>{l}</TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="curriculum" className="mt-6 space-y-4" data-testid="curriculum-content">
              <SectionTitle icon={<BookOpen className="w-5 h-5 text-orange-400" />} title="Why this course?" />
              <p className="text-sm text-slate-400 leading-relaxed mb-6">{course.description}</p>
              {course.curriculum?.map((m, i) => (
                <div key={i} className="bg-card border border-white/10 rounded-xl p-5">
                  <h3 className="font-display font-semibold text-white text-base mb-2">{m.module}</h3>
                  <ul className="space-y-1.5">
                    {m.topics.map((t) => <li key={t} className="text-sm text-slate-400 flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />{t}</li>)}
                  </ul>
                </div>
              ))}
            </TabsContent>
            <TabsContent value="tools" className="mt-6" data-testid="tools-content">
              <SectionTitle icon={<Wrench className="w-5 h-5 text-orange-400" />} title="Tools & Technologies" />
              <div className="flex flex-wrap gap-2.5">
                {course.tools?.map((t) => (
                  <span key={t} className="text-sm font-medium text-slate-200 bg-navy-surface border border-white/15 rounded-full px-4 py-2">{t}</span>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="projects" className="mt-6" data-testid="projects-content">
              <SectionTitle icon={<Wrench className="w-5 h-5 text-orange-400" />} title="Hands-on Projects" />
              <ul className="space-y-3">
                {course.projects?.map((p) => (
                  <li key={p} className="bg-card border border-white/10 rounded-xl p-4 text-sm text-slate-300 flex gap-3"><CheckCircle2 className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />{p}</li>
                ))}
              </ul>
            </TabsContent>
            <TabsContent value="careers" className="mt-6" data-testid="careers-content">
              <SectionTitle icon={<Briefcase className="w-5 h-5 text-orange-400" />} title="Career Opportunities" />
              <p className="text-xs text-slate-500 mb-4">Indicative roles this program prepares you for. XTM provides career guidance and placement assistance — we do not guarantee employment or salary outcomes.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {course.careers?.map((c) => (
                  <div key={c} className="bg-card border border-white/10 rounded-xl p-4 text-sm font-medium text-white">{c}</div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="outcomes" className="mt-6" data-testid="outcomes-content">
              <SectionTitle icon={<Award className="w-5 h-5 text-orange-400" />} title="Learning Outcomes" />
              <ul className="space-y-3">
                {course.outcomes?.map((o) => (
                  <li key={o} className="text-sm text-slate-300 flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />{o}</li>
                ))}
              </ul>
            </TabsContent>
          </Tabs>

          {course.faqs?.length > 0 && (
            <div className="mt-12" data-testid="course-faqs">
              <SectionTitle icon={<BookOpen className="w-5 h-5 text-orange-400" />} title="FAQs" />
              <Accordion type="single" collapsible className="space-y-3">
                {course.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`cf-${i}`} className="bg-card border border-white/10 rounded-xl px-5">
                    <AccordionTrigger className="text-left text-sm font-medium text-white hover:text-orange-300 hover:no-underline" data-testid={`course-faq-${i}`}>{f.q}</AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-400">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <div className="bg-card border border-white/10 rounded-2xl p-6" data-testid="course-batches">
            <h3 className="font-display font-semibold text-white mb-4 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-orange-400" /> Upcoming Batches</h3>
            {course.batches?.length ? (
              <ul className="space-y-3">
                {course.batches.map((b) => (
                  <li key={b.id} className="border border-white/10 rounded-xl p-3.5 text-xs text-slate-300">
                    <div className="flex justify-between mb-1">
                      <span className="font-semibold text-white">{b.batch_code}</span>
                      <span className="text-orange-400 font-semibold">{b.status}</span>
                    </div>
                    <div>Starts {b.start_date} • {b.days}</div>
                    <div className="text-slate-500">{b.time} • {b.mode} • {b.capacity - b.enrolled} seats left</div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500">New batches announced regularly — enquire for the next start date.</p>
            )}
          </div>
          <div className="bg-card border border-white/10 rounded-2xl p-6" data-testid="course-who-join">
            <h3 className="font-display font-semibold text-white mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-orange-400" /> Who Should Join?</h3>
            <p className="text-sm text-slate-400 leading-relaxed">Students meeting the eligibility ({course.eligibility}) who want practical, project-based training and a verifiable certificate. Ideal for learners in Jorhat and Upper Assam — hybrid options available.</p>
          </div>
        </aside>
      </section>

      {course.related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="related-courses">
          <h2 className="font-display text-2xl font-bold text-white mb-8">Related Courses</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {course.related.map((c) => <CourseCard key={c.slug} course={c} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function SectionTitle({ icon, title }) {
  return <h2 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2">{icon}{title}</h2>;
}
